import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Organization, Entity, User, EntityMembership, DisplayMode } from '@/types';

interface AppState {
  // Auth
  user: User | null;
  isAuthenticated: boolean;

  // Multi-tenant
  activeOrg: Organization | null;
  activeEntity: Entity | null;
  entities: Entity[];
  entityMemberships: EntityMembership[];
  displayMode: DisplayMode;

  // Theme
  // - `themeMode` is the user's CHOICE: 'auto' (follow OS), 'light', or 'dark'.
  // - `theme` is what's ACTUALLY applied to the document — `'light' | 'dark'`,
  //   resolved from themeMode + system preference. Components that just want
  //   to know "is the page dark right now" read `theme`; the toggle UI binds
  //   to `themeMode`. Sprint 1.5 (2026-05-28) introduced the 3-state mode.
  themeMode: 'auto' | 'light' | 'dark';
  theme: 'light' | 'dark';

  // Actions
  setUser: (user: User | null) => void;
  setActiveOrg: (org: Organization) => void;
  setActiveEntity: (entity: Entity) => void;
  setEntities: (entities: Entity[]) => void;
  setEntityMemberships: (memberships: EntityMembership[]) => void;
  switchEntity: (entityId: string) => void;
  /** Set the user's theme mode and apply it. Persists to localStorage. */
  setThemeMode: (mode: 'auto' | 'light' | 'dark') => void;
  /** Legacy: cycle light → dark → auto → light. Kept for back-compat; new
   *  callers should use setThemeMode directly. */
  toggleTheme: () => void;
  logout: () => void;
}

// Persisted across reloads so a refresh during a transient backend hiccup
// (Aurora cold start, brief CloudFront 503, etc.) doesn't blow the user
// back to /login. The store is the cached snapshot of the last known
// good /auth/me; ProtectedRoute revalidates in the background and only
// signs out on a true 401 with refresh failure.
//
// Why we persist: without this, every reload starts with user=null, and
// the bootstrap MUST round-trip /auth/me. Any transient failure on that
// call (which previously signed the user out) becomes a recurring
// "Sign-in failed" banner. With persistence + the bootstrap retry
// logic, the user stays signed in across reloads even when the API
// has a 5-second hiccup.
export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      activeOrg: null,
      activeEntity: null,
      entities: [],
      entityMemberships: [],
      displayMode: 'simple',
      // Sprint 1.5 theme init: themeMode is the user's choice; theme is
      // the resolved effective theme (what's applied to the DOM).
      themeMode: (() => {
        if (typeof window === 'undefined') return 'auto';
        // Migration: prior versions stored only `nexus_theme` ('light'|'dark').
        // If user had set that explicitly, honor it as their themeMode.
        const savedMode = localStorage.getItem('nexus_theme_mode');
        if (savedMode === 'auto' || savedMode === 'light' || savedMode === 'dark') return savedMode;
        const legacy = localStorage.getItem('nexus_theme');
        if (legacy === 'light' || legacy === 'dark') return legacy;
        // Default: auto (respect OS). New users get this.
        return 'auto';
      })(),
      theme: (() => {
        if (typeof window === 'undefined') return 'light';
        const savedMode = localStorage.getItem('nexus_theme_mode');
        const legacy = localStorage.getItem('nexus_theme');
        const mode = (savedMode === 'auto' || savedMode === 'light' || savedMode === 'dark')
          ? savedMode
          : (legacy === 'light' || legacy === 'dark') ? legacy : 'auto';
        if (mode === 'light' || mode === 'dark') return mode;
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      })(),

      // Actions
      setUser: (user) => set({ user, isAuthenticated: !!user }),

      setActiveOrg: (org) => set({ activeOrg: org, displayMode: org.displayMode }),

      setActiveEntity: (entity) => {
        set({ activeEntity: entity });
        try { localStorage.setItem('nexus_active_entity', entity.id); } catch { /* */ }
      },

      setEntities: (entities) => set({ entities }),

      setEntityMemberships: (memberships) => set({ entityMemberships: memberships }),

      switchEntity: (entityId) => {
        const entity = useAppStore.getState().entities.find((e) => e.id === entityId);
        if (entity) {
          set({ activeEntity: entity });
          try { localStorage.setItem('nexus_active_entity', entity.id); } catch { /* */ }
        }
      },

      setThemeMode: (mode) =>
        set(() => {
          // Resolve effective theme.
          const effective: 'light' | 'dark' = mode === 'auto'
            ? (typeof window !== 'undefined'
                && window.matchMedia('(prefers-color-scheme: dark)').matches
                ? 'dark' : 'light')
            : mode;
          if (typeof document !== 'undefined') {
            document.documentElement.classList.toggle('dark', effective === 'dark');
          }
          try {
            localStorage.setItem('nexus_theme_mode', mode);
            localStorage.setItem('nexus_theme', effective); // legacy mirror for back-compat
          } catch { /* */ }
          return { themeMode: mode, theme: effective };
        }),

      toggleTheme: () =>
        // Sprint 1.5 (2026-05-28): cycle light → dark → auto → light. Kept
        // for any caller that still wires a single-button toggle; the
        // canonical UI is now the 3-button toggle (SidebarUserFooter).
        set((state) => {
          const next: 'auto' | 'light' | 'dark' =
            state.themeMode === 'light' ? 'dark'
            : state.themeMode === 'dark' ? 'auto'
            : 'light';
          const effective: 'light' | 'dark' = next === 'auto'
            ? (typeof window !== 'undefined'
                && window.matchMedia('(prefers-color-scheme: dark)').matches
                ? 'dark' : 'light')
            : next;
          if (typeof document !== 'undefined') {
            document.documentElement.classList.toggle('dark', effective === 'dark');
          }
          try {
            localStorage.setItem('nexus_theme_mode', next);
            localStorage.setItem('nexus_theme', effective);
          } catch { /* */ }
          return { themeMode: next, theme: effective };
        }),

      logout: () =>
        set({
          user: null,
          isAuthenticated: false,
          activeOrg: null,
          activeEntity: null,
          entities: [],
          entityMemberships: [],
          displayMode: 'simple',
        }),
    }),
    {
      name: 'nexus-app-state',
      storage: createJSONStorage(() => localStorage),
      // Persist only what we want cached across reloads. Theme is already
      // persisted separately via nexus_theme so we exclude it here.
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        activeOrg: state.activeOrg,
        activeEntity: state.activeEntity,
        entities: state.entities,
        entityMemberships: state.entityMemberships,
        displayMode: state.displayMode,
      }),
      version: 1,
    }
  )
);
